//Components
import Header from '../../components/pageHeader.js'

//Styling
import './changeLogs.scss'
import '../../dashboard.scss'

//Functions and Data
import config from './config.js'
import React, { useEffect, useRef, useState } from 'react'
import baseconfig from '../../../../variables/config.js'
import axios from 'axios'

const LIMIT = 50

const ChangeLogs = ({ isOpen }) => {
  const [logs, setLogs] = useState([])
  const [selected, setSelected] = useState(-1)
  const [tabs] = useState([{ name: 'Change Logs' }])
  const [users, setUsers] = useState({})
  const [isUsersLoading, setIsUsersLoading] = useState(false)

  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const [isLoading, setLoading] = useState(false)
  const [unauthorised, setUnauthorised] = useState(false)

  // Pagination can fire multiple requests quickly (scrolling, filter resets).
  // These refs ensure only the latest request is allowed to update UI state.
  const isFetchingRef = useRef(false)
  const requestIdRef = useRef(0)

  const [filters, setFilters] = useState({
    changedBy: '',
    action: '',
    model: '',
    startDate: '',
    endDate: ''
  })

  const [token] = useState(
    JSON.parse(window.localStorage.getItem('userData')).token
  )

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    resetAndFetch()
  }, [filters])

  // Once users are loaded, add `changedByEmail` for display (and if an
  // email-based filter is active, re-fetch so `inq` IDs match the filter).
  useEffect(() => {
    if (Object.keys(users).length === 0) return

    if (filters.changedBy) {
      resetAndFetch()
      return
    }

    setLogs(prev =>
      prev.map(log => ({
        ...log,
        changedByEmail:
          (users[log.changedBy] && users[log.changedBy].email) ||
          log.changedByEmail ||
          log.changedBy
      }))
    )
  }, [users])

  const _url = filter => {
    const encoded = encodeURIComponent(JSON.stringify(filter))
    return `${baseconfig.serverURL}/ChangeLogs?filter=${encoded}&access_token=${token}`
  }

  const buildFilter = () => {
    const where = {}

    if (filters.action) where.action = filters.action

    if (filters.model) where.model = { like: filters.model }

    if (filters.changedBy) {
      const query = filters.changedBy.trim().toLowerCase()
      if (query) {
        // If users are not loaded yet, don't apply the changedBy filter yet.
        // We'll re-fetch once users finish loading.
        if (Object.keys(users).length !== 0) {
          const matchingUserIds = Object.values(users)
            .filter(u => (u.email || '').toLowerCase().includes(query))
            .map(u => u.id)

          if (matchingUserIds.length > 0) {
            // Backend uses LoopBack-style `inq` for "field IN [..]".
            where.changedBy = { inq: matchingUserIds }
          } else {
            // If the input "looks like" a Mongo ObjectId, allow direct ID match.
            const maybeObjectId = filters.changedBy.trim()
            if (/^[0-9a-f]{24}$/i.test(maybeObjectId)) {
              where.changedBy = { inq: [maybeObjectId] }
            } else {
              // No matching email -> empty result set.
              where.changedBy = { inq: [] }
            }
          }
        }
      }
    }

    if (filters.startDate || filters.endDate) {
      where.changedAt = {}

      if (filters.startDate) where.changedAt.gte = new Date(filters.startDate)

      if (filters.endDate) where.changedAt.lte = new Date(filters.endDate)
    }

    return {
      where,
      order: 'changedAt DESC',
      limit: LIMIT,
      skip
    }
  }

  const resetAndFetch = () => {
    setLogs([])
    setSkip(0)
    setHasMore(true)
    fetchLogs(true)
  }

  const fetchUsers = async () => {
    setIsUsersLoading(true)
    try {
      const adminUrl = `${baseconfig.serverURL}/AdminUsers?access_token=${token}`
      const staffUrl = `${baseconfig.serverURL}/StaffUsers?access_token=${token}`

      const [adminRes, staffRes] = await Promise.all([
        axios.get(adminUrl),
        axios.get(staffUrl)
      ])

      const userDict = {}

      adminRes.data.forEach(user => {
        userDict[user.id] = user
      })

      staffRes.data.forEach(user => {
        userDict[user.id] = user
      })

      setUsers(userDict)
    } catch (error) {
      if (error.response && error.response.status === 401) setUnauthorised(true)
      console.error('Error fetching users for change logs:', error)
    } finally {
      setIsUsersLoading(false)
    }
  }

  const fetchLogs = async (reset = false) => {
    if (!reset && (isFetchingRef.current || !hasMore)) return

    const requestId = ++requestIdRef.current
    isFetchingRef.current = true
    setLoading(true)

    try {
      const filter = buildFilter()
      if (reset) filter.skip = 0

      const response = await axios.get(_url(filter))

      const newLogs = response.data.map(log => ({
        ...log,
        // Keep `changedBy` as the original user ID and add a UI-friendly email.
        changedByEmail:
          (users[log.changedBy] && users[log.changedBy].email) || log.changedBy
      }))

      if (newLogs.length < LIMIT) setHasMore(false)

      // Ignore responses from stale requests (a newer fetch started).
      if (requestId !== requestIdRef.current) return

      setLogs(prev => (reset ? newLogs : [...prev, ...newLogs]))
      setSkip(prev => prev + LIMIT)
    } catch (error) {
      // Ignore stale request errors as well.
      if (requestId === requestIdRef.current) {
        if (error.response && error.response.status === 401) setUnauthorised(true)
        console.error(error)
      }
    } finally {
      // Only clear loading for the latest request.
      if (requestId === requestIdRef.current) setLoading(false)
      if (requestId === requestIdRef.current) isFetchingRef.current = false
    }
  }

  const handleScroll = e => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50

    if (bottom) fetchLogs()
  }

  const handleFilterChange = (e, field) => {
    const { value } = e.target

    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const toggle = index => setSelected(selected === index ? -1 : index)

  const renderChanges = log => {
    const action = (log.action || '').toLowerCase()

    // For create/delete logs, the backend typically provides the full created
    // / deleted entry in `after` / `before` rather than a per-field diff.
    if (action === 'create') {
      if (!log.after) return null
      return (
        <div className="logBlock">
          <div className="logTitle">After (Created)</div>
          <pre className="logJson">{JSON.stringify(log.after, null, 2)}</pre>
        </div>
      )
    }

    if (action === 'delete') {
      if (!log.before) return null
      return (
        <div className="logBlock">
          <div className="logTitle">Before (Deleted)</div>
          <pre className="logJson">{JSON.stringify(log.before, null, 2)}</pre>
        </div>
      )
    }

    if (!log.changes) return null

    return Object.keys(log.changes).map(field => {
      const before = log.before && log.before[field]
      const after = log.after && log.after[field]

      return (
        <div key={field} className="changeRow">
          <div className="changeField">{field}</div>

          <div className="changeValues">
            <span className="before">{JSON.stringify(before)}</span>

            <span className="arrow">→</span>

            <span className="after">{JSON.stringify(after)}</span>
          </div>
        </div>
      )
    })
  }

  const renderLogs = () =>
    logs.map((log, index) => (
      <div key={log.id} className="listItem">
        <div className="itemRow dateObjHeader">
          <div className="logMain">
            {log.action.toUpperCase()} — {log.model}
          </div>

          <div className="logMeta">
            User: {log.changedByEmail || 'Unknown'}
          </div>

          <div className="logMeta">
            {new Date(log.changedAt).toLocaleString()}
          </div>

          <div className="buttonWrapper" onClick={() => toggle(index)}>
            {selected === index ? 'Hide Details' : 'Show Details'}
          </div>
        </div>

        {selected === index && (
          <div className="logDetails">
            <div className="logModelId">Model ID: {log.modelId}</div>

            {renderChanges(log)}
          </div>
        )}
      </div>
    ))

  return (
    <div className={'component' + (isOpen ? '' : ' componentClosed')}>
      {!unauthorised && (
        <>
          <Header
            title={config.mainTitle}
            tip={config.subHeading}
            icon={config.titleIcon}
            tabs={tabs}
            selected={tabs[0]}
          />

          <div className="filters">
            <input
              placeholder="Search by User Email"
              value={filters.changedBy}
              onChange={e => handleFilterChange(e, 'changedBy')}
              disabled={isUsersLoading}
            />

            <select
              value={filters.action}
              onChange={e => handleFilterChange(e, 'action')}>
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
            </select>

            <input
              placeholder="Model"
              value={filters.model}
              onChange={e => handleFilterChange(e, 'model')}
            />

            <input
              type="date"
              value={filters.startDate}
              onChange={e => handleFilterChange(e, 'startDate')}
            />

            <input
              type="date"
              value={filters.endDate}
              onChange={e => handleFilterChange(e, 'endDate')}
            />
          </div>

          <div className="scrollView" onScroll={handleScroll}>
            {renderLogs()}

            {isLoading && <div className="loadingText">Loading Logs...</div>}

            {!hasMore && <div className="loadingText">End of Logs</div>}
          </div>
        </>
      )}

      {unauthorised && (
        <div className="unauthorisedMessage">
          You are not authorised to view logs.
        </div>
      )}
    </div>
  )
}

export default ChangeLogs
